import { compareByFixType } from "@/helpers/compare.helper"

import type { PackageRecord } from "@/api/useIssues.types"
import type {
  CodeRepoFinding,
  GitMetadata,
  PackageFixRecord
} from "@/domain/issue"
import type { SourceItem } from "./SourceList.types"

export const findingsToSources = (findings: CodeRepoFinding[]) => {
  // grouped findings by source
  const grouped = findings.reduce(
    (acc, curr) => {
      if (!acc[curr.source]) {
        acc[curr.source] = {
          remedies: [],
          introducedThrough: [],
          gitMetadata: null,
          computationError: null,
          packageName: "",
          sourceControlLink: "",
          resolutionData: {
            commitHash: null,
            resolvedBy: null,
            resolvedAt: 0
          },
          isResolved: null,
          isResolvedInCodeRepoOnly: false
        }
      }

      acc[curr.source].remedies = [
        ...acc[curr.source].remedies,
        ...(curr.remedies || [])
      ]
      acc[curr.source].introducedThrough = [
        ...acc[curr.source].introducedThrough,
        ...curr.introducedThrough
      ]

      if (curr.packageName) {
        acc[curr.source].packageName = curr.packageName
      }

      if (curr.fixVersions && curr.fixVersions.length > 0) {
        acc[curr.source].fixVersions = curr.fixVersions
      }

      if (curr.computationError) {
        acc[curr.source].computationError = curr.computationError
      }

      if (curr.sourceControlLinkToSource) {
        acc[curr.source].sourceControlLink = curr.sourceControlLinkToSource
      }

      if (curr.resolutionData && findings.length > 1) {
        acc[curr.source].resolutionData = curr.resolutionData
        acc[curr.source].isResolved = curr.isResolved
      }

      if (curr.gitMetadata) {
        acc[curr.source].gitMetadata = curr.gitMetadata
      }

      acc[curr.source].isResolvedInCodeRepoOnly =
        curr.isResolvedInCodeRepoOnly ?? false

      return acc
    },
    {} as Record<
      string,
      {
        remedies: PackageFixRecord[]
        introducedThrough: PackageRecord[][]
        packageName: string
        fixVersions?: string[]
        computationError: string | null
        sourceControlLink: string
        resolutionData: {
          resolvedAt: number
          commitHash: string | null
          resolvedBy: string | null
        }
        isResolved: boolean | null
        gitMetadata: GitMetadata | null
        isResolvedInCodeRepoOnly: boolean | undefined
      }
    >
  )

  const sources: SourceItem[] = Object.entries(grouped)
    .map(([key, val]) => {
      return {
        name: key,
        remedies: val.remedies.sort(compareByFixType),
        introducedThrough: val.introducedThrough,
        packageName: val.packageName,
        fixVersions: val.fixVersions,
        computationError: val.computationError,
        sourceControlLink: val.sourceControlLink,
        resolutionData: val.resolutionData,
        isResolved: val.isResolved,
        gitMetadata: val.gitMetadata,
        isResolvedInCodeRepoOnly: val.isResolvedInCodeRepoOnly
      }
    })
    .sort((a, b) => {
      if (a.remedies.length == 0) {
        return 1
      }
      if (b.remedies.length == 0) {
        return -1
      }

      const fixTypePriority = compareByFixType(a.remedies[0], b.remedies[0])
      if (fixTypePriority !== 0) return fixTypePriority

      return a.name.localeCompare(b.name)
    })

  return sources
}
